import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
// Redux actions
import { getCategoriesFunc } from "../../../store/custom-actions/categories-actions/GetCategoriesSlicer";
import { getSingleCategoryFunc } from "../../../store/custom-actions/categories-actions/GetSingleCategorySlicer";
import { updateCategoryFunc } from "../../../store/custom-actions/categories-actions/UpdateCategorySlicer";

// UI
import PageTitle from "../../layouts/PageTitle";
import FormField from "../../components/ui/FormField";
import CheckboxField from "../../components/ui/CheckboxField";
import SubmitButton from "../../components/ui/SubmitButton";
import NestedTreeSelector from "../../components/ui/NestedTreeSelector";
import LanguageSwitcherTabs from "../../components/ui/LanguageSwitcherTabs";
import MediaSelectorButton from "../../components/media/MediaSelectorButton";

function UpdateCategoryForm() {
  const { t } = useTranslation();
  const { catId } = useParams();
  const dispatch = useDispatch();

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm();

  // Redux states
  const { loading: submitLoading } = useSelector(
    (state) => state.updateCategorySlicer
  );
  const { loading, data: categoriesList } = useSelector(
    (state) => state.getCategoriesSlicer
  );
  const { loading: singleCategoryLoading } = useSelector(
    (state) => state.getSingleCategorySlicer
  );
  const { loading: languageLoading, data: languageData } = useSelector(
    (state) => state.getLanguageSettingsSlicer
  );

  // Local state
  const [categoryData, setCategoryData] = useState({});
  const [parentId, setParentId] = useState(null);
  const [activeLang, setActiveLang] = useState();
  const [thumbnailDefault, setThumbnailDefault] = useState([]);
  // Fetch all categories
  useEffect(() => {
    dispatch(getCategoriesFunc());
  }, [dispatch]);

  // Load single category data
  useEffect(() => {
    if (!catId) return;

    const fetchCategory = async () => {
      const result = await dispatch(getSingleCategoryFunc(catId));
      if (getSingleCategoryFunc.fulfilled.match(result)) {
        const category = result.payload;
        setCategoryData(category);
        setValue("active", category.active);
        setParentId(category.parent_id);
        setThumbnailDefault(category.thumbnail ? [category.thumbnail] : []);
      } else {
        console.error("Failed to fetch category:", result.payload);
      }
    };

    fetchCategory();
  }, [catId, dispatch, setValue]);

  useEffect(() => {
    if (languageData?.defaultLang?.slug) {
      setActiveLang(languageData.defaultLang.slug);
    }
  }, [languageData]);

  useEffect(() => {
    if (!categoryData?.translations || !activeLang) return;

    const translation = categoryData.translations.find(
      (tr) => tr.locale === activeLang
    );
    setValue("name", translation?.name || "");
  }, [activeLang, categoryData, setValue]);

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      parent_id: parentId,
      lang: activeLang,
    };

    await dispatch(updateCategoryFunc({ id: catId, data: payload }));
  };
  return (
    <>
      <PageTitle
        motherMenu={"Categories"}
        activeMenu={"Update Category"}
        motherMenuPath="/categories"
      />
      <div className="row">
        <div className="col-xl-12 col-xxl-12">
          <LanguageSwitcherTabs
            languages={languageData?.languages || []}
            activeLang={activeLang}
            onChange={setActiveLang}
          />
          <div className="card">
            <div className="card-header">
              <h5 class="card-title">{t("Update") + " " + t("Category")}</h5>
            </div>
            <div className="card-body">
              <form id="submit-form" onSubmit={handleSubmit(onSubmit)}>
                <FormField
                  label={`${t("Name")} (${activeLang})`}
                  name="name"
                  register={register}
                  required={true}
                  errors={errors}
                />

                <div className="form-group mb-3 col-sm-12 col-md-6">
                  <MediaSelectorButton
                    label="Category Image"
                    inputName="thumbnail_img"
                    register={register}
                    errors={errors}
                    onChange={(value) => setValue("thumbnail_img", value)}
                    defaultSelected={thumbnailDefault}
                  />
                </div>

                <div className="form-group mb-3">
                  <label>{t("Main Category")}</label>
                  <NestedTreeSelector
                    treeData={categoriesList}
                    onSelect={setParentId}
                    loading={loading}
                    defaultValue={categoryData.parent_id}
                  />
                </div>
                <CheckboxField
                  name="active"
                  label="Activity"
                  register={register}
                  errors={errors}
                />
                <div className="text-end">
                  <SubmitButton isLoading={submitLoading} form="submit-form" />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default UpdateCategoryForm;
